<script>
    import { fade, fly } from 'svelte/transition';
    import { createEventDispatcher } from 'svelte';

    const dispatch = createEventDispatcher();

    export let isVisible = false;
    export let stepsRemaining = 0;
    export let totalSteps = 0;
    export let estimatedTime = null; // in seconds
    export let progress = 0; // 0-100

    $: stepsComplete = Math.max(0, totalSteps - stepsRemaining);

    function formatTime(seconds) {
        if (!seconds || seconds < 0) return '';

        if (seconds < 60) {
            return `${Math.ceil(seconds)}s`;
        }

        const minutes = Math.floor(seconds / 60);
        const secs = seconds % 60;

        if (secs === 0) {
            return `${minutes}m`;
        }

        return `${minutes}m ${Math.ceil(secs)}s`;
    }

    function handleStop() {
        dispatch('stop');
    }
</script>

{#if isVisible}
    <div
        class="term-progress-bar"
        transition:fly={{ y: 20, duration: 200 }}
    >
        <div class="progress-content">
            <div class="progress-track">
                <div
                    class="progress-fill"
                    style="width: {progress}%"
                />
            </div>
            <div class="progress-info">
                <div class="progress-text">
                    {#if totalSteps > 0}
                        {#if stepsComplete === totalSteps && stepsRemaining === 0}
                            <span class="steps-info">Refining results...</span>
                        {:else}
                            <span class="steps-info">{stepsComplete}/{totalSteps} steps complete</span>
                        {/if}
                    {/if}
                    {#if estimatedTime}
                        <span class="time-info">Expected time remaining: {formatTime(estimatedTime)}</span>
                    {/if}
                </div>
                <button class="stop-button" on:click={handleStop} title="Stop term analysis">
                    Click to stop
                </button>
            </div>
        </div>
    </div>
{/if}

<style>
.term-progress-bar {
    position: absolute;
    right: var(--spacing-2);
    bottom: var(--spacing-2);
    left: var(--spacing-2);
    z-index: 10;
    background: var(--color-term-progress-bg);
    border: 1px solid var(--color-term-progress-border);
    border-radius: var(--border-radius-lg);
    box-shadow: 0 2px 12px var(--color-term-progress-shadow);
    backdrop-filter: blur(8px);
}

.progress-content {
    padding: var(--spacing-2) var(--spacing-3);
}

.progress-track {
    height: 4px;
    margin-bottom: var(--spacing-1);
    overflow: hidden;
    background: var(--color-term-progress-track);
    border-radius: 2px;
}

.progress-fill {
    position: relative;
    height: 100%;
    overflow: hidden;
    background: linear-gradient(90deg,
        var(--color-term-progress-fill-start) 0%,
        var(--color-term-progress-fill-end) 100%);
    border-radius: 2px;
    transition: width 0.3s ease-out;
}

.progress-fill::after {
    position: absolute;
    inset: 0;
    background: linear-gradient(
        90deg,
        transparent 0%,
        var(--color-term-progress-shimmer) 50%,
        transparent 100%
    );
    animation: shimmer 2s linear infinite;
    content: '';
}

@keyframes shimmer {
    0% {
        transform: translateX(-100%);
    }

    100% {
        transform: translateX(100%);
    }
}

.progress-info {
    display: flex;
    align-items: center;
    padding: 0 var(--spacing-1);
    font-size: 11px;
    color: var(--color-text-secondary);
}

.progress-text {
    display: flex;
    align-items: center;
    gap: var(--spacing-2);
}

.steps-info {
    font-weight: 500;
    color: var(--color-term-progress-steps);
}

.time-info {
    color: var(--color-term-progress-time);
    opacity: var(--color-term-progress-time-opacity);
}

.stop-button {
    padding: var(--spacing-1) var(--spacing-2);
    margin-left: auto;
    font-size: 11px;
    white-space: nowrap;
    color: var(--color-text-secondary);
    background: transparent;
    border: none;
    border-radius: var(--border-radius-md);
    transition: all 0.2s ease;
    cursor: pointer;
}

.stop-button:hover {
    font-weight: 700;
    text-decoration: underline;
    color: var(--color-error);
}
</style>
